import "./CreateLegislation.css";

import {
  Breadcrumb,
  Button,
  Divider,
  Form,
  Input,
  message,
  Select,
} from "antd";
import { useEffect, useState } from "react";
import { BsTrash } from "react-icons/bs";
import { CgScreen } from "react-icons/cg";
import { IoMdAdd } from "react-icons/io";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  type CategoryType,
  createLegislation,
  deleteLegislation,
  getCategories,
  getPackages,
  getSubcategories,
  type LegislationFormType,
  type LegislationType,
  type SubCategoryType,
  updateLegislation,
  useAppSelector,
} from "../../../_lib/_store";

interface CreateLegislationPageProps {
  type: "create" | "edit";
}

export const CreateLegislationPage = ({ type }: CreateLegislationPageProps) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { isLoading } = useAppSelector((state) => state.legislation);
  const { isLoading: packageLoading, packages } = useAppSelector(
    (state) => state.package,
  );
  const { isLoading: categoryLoading, categories } = useAppSelector(
    (state) => state.category,
  );
  const { isLoading: subcategoryLoading, subCategories } = useAppSelector(
    (state) => state.subCategory,
  );

  const [filteredCategories, setFilteredCategories] = useState<CategoryType[]>(
    [],
  );
  const [filteredSubCategories, setFilteredSubCategories] = useState<
    SubCategoryType[]
  >([]);
  const [linkList, setLinkList] = useState<
    { url: string; title: string; description: string }[]
  >([]);

  const navigate = useNavigate();
  const { state } = useLocation();

  const isEdit = type === "edit";

  useEffect(() => {
    const legislationObj = state?.legislation;
    if (isEdit && !legislationObj) {
      navigate("/policy");
    }
    if (isEdit) {
      form.setFieldsValue(legislationObj);
      if (legislationObj?.packageId) {
        setFilteredCategories(
          categories.filter((c) => c.packageId === legislationObj?.packageId),
        );
      }
      if (legislationObj?.categoryId) {
        setFilteredSubCategories(
          subCategories.filter(
            (s) => s.categoryId === legislationObj.categoryId,
          ),
        );
      }
      setLinkList(legislationObj?.linkList);
    }
  }, [state, packages, categories, subCategories]);

  useEffect(() => {
    getData();
  }, [state]);

  const getData = async () => {
    await getPackages(messageApi);
    await getCategories(messageApi);
    await getSubcategories(messageApi);
  };

  const handleValuesChange = (changedValues: LegislationType) => {
    if (changedValues.packageId) {
      setFilteredCategories(
        categories.filter((c) => c.packageId === changedValues.packageId),
      );
      form.setFieldsValue({
        categoryId: undefined,
        subCategoryId: undefined,
      });
      setFilteredSubCategories([]);
    }

    if (changedValues.categoryId) {
      setFilteredSubCategories(
        subCategories.filter((s) => s.categoryId === changedValues.categoryId),
      );
      form.setFieldsValue({ subCategoryId: undefined });
    }
    if (changedValues.subCategoryId) {
      setFilteredSubCategories(subCategories);
    }
  };

  const handleSubmit = async (values: LegislationFormType) => {
    if (linkList.length > 0) {
      for (let i = 0; i < linkList.length; i++) {
        const link = linkList[i];
        if (!link.url || !link.title) {
          messageApi.error(
            `Link ${i + 1}: "URL" and "Title" are required or remove this link`,
          );
          return;
        }
        if (!/^https?:\/\/[^\s$.?#].[^\s]*$/i.test(link.url)) {
          messageApi.error(`Link ${i + 1}: URL must be valid (http/https)`);
          return;
        }
      }
    }
    const payload = {
      ...values,
      linkList,
      ...(isEdit ? { id: state.legislation.id } : {}),
    };

    if (isEdit) {
      const res = await updateLegislation(messageApi, payload);
      if (res) {
        navigate("/legislation-link/edit", {
          state: { legislation: res },
          replace: true,
        });
      }
    } else {
      const res = await createLegislation(messageApi, payload);
      if (res) {
        form.resetFields();
        setLinkList([]);
      }
    }
  };

  const handleDelete = async () => {
    const res = await deleteLegislation(messageApi, state.legislation.id);
    if (res) {
      navigate("/legislation-link");
    }
  };

  const handleAddLink = () => {
    setLinkList([...linkList, { url: "", title: "", description: "" }]);
  };
  const handleChangeLink = (
    index: number,
    field: "url" | "title" | "description",
    value: string,
  ) => {
    const newList = [...linkList];
    newList[index][field] = value;
    setLinkList(newList);
  };
  const handleRemoveLink = (index: number) => {
    setLinkList(linkList.filter((_, i) => i !== index));
  };

  return (
    <>
      {contextHolder}
      <div className="createPackage">
        <Breadcrumb
          items={[
            {
              title: <Link to="/dashboard">Dashboard</Link>,
            },
            {
              title: <Link to="/legislation-link">Legislation Link</Link>,
            },
            {
              title: isEdit ? "Edit" : "Create new",
            },
          ]}
        />

        {!isEdit ? (
          <h2 className="title">Create new</h2>
        ) : (
          <div className="titleHeader">
            <h2 className="title">Edit</h2>
            <div className="actionButton">
              <Button
                icon={<CgScreen size={17} />}
                size="large"
                onClick={() =>
                  navigate(`/legislation-link/show`, {
                    state: { legislation: state?.legislation },
                  })
                }
              >
                Show
              </Button>
              <Button
                icon={<BsTrash />}
                size="large"
                onClick={handleDelete}
                loading={isLoading}
              >
                Delete
              </Button>
            </div>
          </div>
        )}

        <div className="form">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={handleValuesChange}
          >
            <Form.Item
              label="Title"
              name="title"
              rules={[
                { required: true, message: "Legislation title is required" },
              ]}
              tooltip={"Legislation title is required"}
            >
              <Input size="large" />
            </Form.Item>

            <Form.Item
              label="Url"
              name="url"
              rules={[
                { required: true, message: "Legislation URL is required" },
                { type: "url", message: "Please enter a valid URL" },
              ]}
              tooltip={"Legislation URL is required"}
            >
              <Input size="large" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <Input size="large" />
            </Form.Item>

            <Form.Item label="Site Name" name="siteName">
              <Input size="large" />
            </Form.Item>

            <Form.Item
              label="Package"
              name="packageId"
              rules={[{ required: true, message: "Package is Required" }]}
              help={
                form.getFieldValue("packageId") ? "" : "This field is required."
              }
            >
              <Select
                loading={packageLoading}
                size="large"
                options={packages?.map((v) => ({
                  label: v?.name,
                  value: v?.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Category"
              name="categoryId"
              rules={[{ required: true, message: "Category is Required" }]}
              help={
                form.getFieldValue("categoryId")
                  ? ""
                  : "Please select a package first to enable category selection and this field is required."
              }
            >
              <Select
                loading={categoryLoading}
                size="large"
                options={filteredCategories.map((c) => ({
                  label: c?.name,
                  value: c?.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Sub Category"
              name="subCategoryId"
              help={
                form.getFieldValue("subCategoryId")
                  ? ""
                  : "Please select a category first to enable subcategory selection."
              }
            >
              <Select
                loading={subcategoryLoading}
                size="large"
                options={filteredSubCategories.map((s) => ({
                  label: s?.name,
                  value: s?.id,
                }))}
              />
            </Form.Item>

            <Form.Item label="Link List" name="linkList">
              <div className="linkBox">
                {linkList.map((link, i) => (
                  <div key={i} className="linkInputBox">
                    <h4>Link {i + 1}</h4>

                    <Form.Item
                      label="URL"
                      required
                      validateStatus={!link.url ? "error" : ""}
                      help={
                        !link.url
                          ? "URL cannot be empty"
                          : !/^https?:\/\/[^\s$.?#].[^\s]*$/i.test(link.url)
                            ? "Enter a valid URL (must start with http:// or https://)"
                            : ""
                      }
                    >
                      <Input
                        size="large"
                        placeholder="Enter URL"
                        value={link.url}
                        onChange={(e) =>
                          handleChangeLink(i, "url", e.target.value)
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      label="Title"
                      required
                      validateStatus={!link.title ? "error" : ""}
                      help={!link.title ? "Title cannot be empty" : ""}
                    >
                      <Input
                        size="large"
                        placeholder="Enter title"
                        value={link.title}
                        onChange={(e) =>
                          handleChangeLink(i, "title", e.target.value)
                        }
                      />
                    </Form.Item>

                    <Form.Item label="Description">
                      <Input
                        size="large"
                        placeholder="Enter description"
                        value={link.description}
                        onChange={(e) =>
                          handleChangeLink(i, "description", e.target.value)
                        }
                      />
                    </Form.Item>

                    <Button
                      danger
                      variant="outlined"
                      onClick={() => handleRemoveLink(i)}
                    >
                      Remove
                    </Button>

                    {i !== linkList.length - 1 && (
                      <Divider style={{ marginBottom: 0 }} />
                    )}
                  </div>
                ))}

                <Button
                  icon={<IoMdAdd />}
                  size="large"
                  className="addButton"
                  onClick={handleAddLink}
                >
                  Add Link
                </Button>
              </div>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{ width: "150px" }}
                loading={isLoading}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
};
